import {
  type RevisionHistory,
  type RevisionHistoryItems,
  type TableRow,
} from "@/components/DirectorySubmissions/Form/types";
import { type SupabaseClient } from "@supabase/supabase-js";

/**
 * Determine if a question ID is for a matter
 */
export const isMatterQuestion = (questionId: string): boolean => {
  return questionId.startsWith("PM") || questionId.startsWith("CM");
};

/**
 * Generate a unique storage key for a revision based on its question ID and matter ID
 * For matter questions, we use the matter ID directly as the key
 * For regular questions, we use the question ID
 */
export const getRevisionStorageKey = (
  questionId: string,
  matterId?: number | null,
): string => {
  // If we have a matter ID, use that directly as the key
  if (matterId) {
    return matterId.toString();
  }
  // Otherwise use the question ID
  return questionId;
};

/**
 * Find the matter ID for a given position-based question ID (PM1, CM2, etc.)
 *
 * @param questionId The position-based question ID (PM1, CM2, etc.)
 * @param submissionId The submission ID
 * @param supabase The Supabase client
 * @returns The matter ID if found, null otherwise
 */
export const findMatterIdByPositionId = async (
  questionId: string,
  submissionId: number | string,
  supabase: SupabaseClient,
): Promise<number | null> => {
  if (!isMatterQuestion(questionId)) {
    return null;
  }

  // Parse position and type from the question ID
  const isPublic = questionId.startsWith("PM");
  const position = parseInt(questionId.replace(/^[A-Z]+/, ""));

  if (isNaN(position)) {
    return null;
  }

  // Query the database to find the matter at this position
  const { data: matters, error } = await supabase
    .from("directory_matter")
    .select("id, is_public, included_order")
    .eq("directory_submission_id", submissionId)
    .order("included_order", { ascending: true });

  if (error || !matters || matters.length === 0) {
    return null;
  }

  // Filter matters by public/confidential status and sort by position
  const filteredMatters = matters
    .filter((m) => m.is_public === isPublic)
    .sort((a, b) => (a.included_order || 0) - (b.included_order || 0));

  // Get the matter at the requested position (0-indexed array, 1-indexed position)
  if (position > 0 && position <= filteredMatters.length) {
    return filteredMatters[position - 1].id;
  }

  return null;
};

/**
 * Create a revision history entry, handling matter IDs appropriately
 */
export const createRevision = async ({
  supabase,
  submissionId,
  userId,
  firmId,
  questionId,
  formData,
  previousValue,
  newValue,
  matterId: explicitMatterId,
  columnIndex,
  rowIndex,
  previousRowIndex,
  kind,
  isCurrent = true,
}: {
  supabase: SupabaseClient;
  submissionId: number | string;
  userId: string;
  firmId: string;
  questionId: string;
  formData?: Record<string, any>;
  previousValue: string | Record<string, any> | null;
  newValue: string | Record<string, any> | null;
  matterId?: number | null;
  columnIndex?: number | null;
  rowIndex?: number | null;
  previousRowIndex?: number | null;
  kind?: string;
  isCurrent?: boolean;
}): Promise<RevisionHistory | null> => {
  try {
    // Ensure submissionId is a number for database consistency
    const numericSubmissionId =
      typeof submissionId === "string"
        ? parseInt(submissionId, 10)
        : submissionId;

    if (isNaN(numericSubmissionId)) {
      console.error("Invalid submission ID for revision:", submissionId);
      return null;
    }

    // Check if this is a matter question
    const isMatter = isMatterQuestion(questionId);

    // For matter questions, we need to get or verify the matter ID
    let matterId = explicitMatterId;

    // If this is a matter and no explicit ID was provided, try to extract from formData
    if (isMatter && !matterId && formData) {
      matterId = extractMatterIdFromFormData(questionId, formData);
    }

    // If we still don't have a matter ID for a matter question, try to look it up by position
    if (isMatter && !matterId) {
      matterId = await findMatterIdByPositionId(
        questionId,
        numericSubmissionId,
        supabase,
      );

      if (!matterId) {
        return null;
      }
    }

    // Prepare the values for storing in the database
    const previousJson =
      typeof previousValue === "object"
        ? previousValue
        : typeof previousValue === "string" ||
            typeof previousValue === "boolean"
          ? previousValue
          : null;

    const newJson =
      typeof newValue === "object"
        ? newValue
        : typeof newValue === "string" || typeof newValue === "boolean"
          ? newValue
          : null;

    // Create the revision in the database
    const response = await supabase
      .from("directory_submission_question_revision_history")
      .insert({
        submission_id: numericSubmissionId,
        question_id: questionId, // Store the position/question ID (PM1, B5, etc.)
        matter_id: isMatter ? matterId : null, // Only set for matters
        previous: previousJson,
        new: newJson,
        created_by: userId,
        firm_id: firmId,
        column_index: columnIndex,
        row_index: rowIndex,
        previous_row_index: previousRowIndex,
        kind,
        is_current: isCurrent,
      })
      .select(
        `
        id,
        created_at,
        previous,
        new,
        is_current,
        question_id,
        matter_id,
        column_index,
        row_index,
        created_by (
          id,
          first_name,
          last_name,
          email,
          avatar_url
        )
      `,
      )
      .returns<RevisionHistory[]>()
      .single();

    if (response.error) {
      console.error("Error creating revision:", response.error);
      return null;
    }

    return response.data;
  } catch (error) {
    console.error("Error in createRevision:", error);
    return null;
  }
};

/**
 * Create a revision history entry and return a boolean success status
 * This is a simplified wrapper for easier integration with forms
 */
export const createRevisionEntry = async ({
  submissionId,
  questionId,
  formData,
  previousValue,
  newValue,
  userId,
  firmId,
  supabase,
  columnIndex,
  rowIndex,
  previousRowIndex,
  kind,
}: {
  submissionId: string;
  questionId: string;
  formData?: Record<string, any>;
  previousValue: any;
  newValue: any;
  userId: string;
  firmId: string;
  supabase: SupabaseClient;
  columnIndex?: number | null;
  rowIndex?: number | null;
  previousRowIndex?: number | null;
  kind?: string;
}): Promise<boolean> => {
  try {
    const result = await createRevision({
      supabase,
      submissionId,
      userId,
      firmId,
      questionId,
      formData,
      previousValue,
      newValue,
      columnIndex,
      rowIndex,
      previousRowIndex,
      kind,
    });

    return result !== null;
  } catch (error) {
    console.error("Error creating revision entry:", error);
    return false;
  }
};

/**
 * Loads and formats revision history data for a directory submission,
 * organizing them by matter ID or question ID
 *
 * @param submissionId - The ID of the submission
 * @param supabase - The Supabase client
 * @returns A record of revision histories organized by matter ID or question ID
 */
export const loadRevisions = async (
  submissionId: string | number,
  supabase: SupabaseClient,
): Promise<RevisionHistoryItems> => {
  try {
    // Fetch raw revisions from database
    const response = await supabase
      .from("directory_submission_question_revision_history")
      .select(
        `
          id,
          created_at,
          previous,
          new,
          is_current,
          question_id,
          matter_id,
          column_index,
          row_index,
          previous_row_index,
          kind,
          created_by (
            id,
            first_name,
            last_name,
            email,
            avatar_url
          )
        `,
      )
      .eq("submission_id", submissionId)
      .returns<RevisionHistory[]>();

    if (response.error) {
      console.error("Error loading revisions:", response.error);
      throw response.error;
    }

    const revisions = response.data || [];

    // Group revisions by their storage key
    const revisionsByKey: RevisionHistoryItems = {};

    if (revisions) {
      revisions.forEach((revision) => {
        // Generate storage key based on the new approach
        const revisionKey = revision.matter_id
          ? revision.matter_id.toString() // For matter questions, use matter ID directly
          : revision.question_id; // For regular questions, use question ID

        if (!revisionsByKey[revisionKey]) {
          revisionsByKey[revisionKey] = [];
        }

        revisionsByKey[revisionKey].push(revision);
      });
    }

    return revisionsByKey;
  } catch (error) {
    console.error("Error in loadRevisions:", error);
    return {};
  }
};

/**
 * Extract matter ID from form data for a matter question
 */
export const extractMatterIdFromFormData = (
  questionId: string,
  formData?: Record<string, any>,
): number | null => {
  if (!formData?.[questionId]) {
    return null;
  }

  const matterData = formData[questionId];
  if (typeof matterData === "object" && "id" in matterData) {
    return matterData.id;
  }

  return null;
};

/**
 * Find all revisions for a question (regular or matter)
 */
export const findRevisionsForQuestion = (
  questionId: string,
  allRevisions: RevisionHistoryItems,
  formData?: Record<string, any>,
): RevisionHistory[] => {
  // Check if this is a matter question
  const isMatter = isMatterQuestion(questionId);

  if (!isMatter) {
    // Regular question - just use the question ID as the key
    const revisions = allRevisions[questionId] || [];
    return revisions;
  }

  // Use the utility function to extract matter ID
  const matterId = extractMatterIdFromFormData(questionId, formData);

  if (!matterId) {
    console.log(
      `No matter ID found for ${questionId}, returning empty revisions array`,
    );
    return [];
  }

  // For matter questions, use the matter ID directly as the key
  const storageKey = matterId.toString();

  const revisions = allRevisions[storageKey] || [];

  return revisions;
};

export const createReorderRevision = async ({
  submissionId,
  questionId,
  userId,
  firmId,
  supabase,
  movedRow, // The row object that was moved
  previousRowIndex, // The original index (0-based)
  rowIndex, // The new index (0-based)
}: {
  submissionId: string;
  questionId: string;
  userId: string;
  firmId: string;
  supabase: SupabaseClient;
  movedRow: TableRow;
  previousRowIndex: number;
  rowIndex: number;
}) => {
  await createRevisionEntry({
    submissionId,
    questionId,
    userId,
    firmId,
    supabase,
    kind: "ROW_REORDER",
    previousValue: null, // The row data itself did not change
    newValue: movedRow.value,
    rowIndex,
    previousRowIndex,
  });
};
